/**
 * PPG (Photoplethysmography) signal processing.
 * Camera + fingertip + LED torch -> red-channel time series -> HR + HRV.
 *
 * All processing local. No raw video or signal leaves device.
 * Output: BPM + RMSSD (millisecond RR interval variability).
 */

export interface PpgSample {
  t: number;
  red: number;
}

export interface HrvResult {
  bpm: number;
  rmssd_ms: number;
  rr_intervals_ms: number[];
  sample_count: number;
  duration_s: number;
  signal_quality: 'good' | 'fair' | 'poor';
}

export class PpgRecorder {
  #samples: PpgSample[] = [];
  #started: number = 0;

  start(): void {
    this.#samples = [];
    this.#started = performance.now();
  }

  push(red: number): void {
    if (this.#started === 0) this.start();
    this.#samples.push({ t: performance.now() - this.#started, red });
  }

  duration(): number {
    return this.#samples.length === 0 ? 0 : (this.#samples[this.#samples.length - 1]?.t ?? 0) / 1000;
  }

  samples(): readonly PpgSample[] {
    return this.#samples;
  }

  compute(): HrvResult {
    return computeHrv(this.#samples);
  }
}

export function computeHrv(samples: readonly PpgSample[]): HrvResult {
  if (samples.length < 30) {
    return {
      bpm: 0,
      rmssd_ms: 0,
      rr_intervals_ms: [],
      sample_count: samples.length,
      duration_s: 0,
      signal_quality: 'poor',
    };
  }
  const detrended = detrend(samples.map((s) => s.red));
  const smoothed = movingAverage(detrended, 5);
  const peaks = findPeaks(smoothed, samples);
  const rrs: number[] = [];
  for (let i = 1; i < peaks.length; i++) {
    const prev = peaks[i - 1];
    const cur = peaks[i];
    if (prev === undefined || cur === undefined) continue;
    const interval = cur - prev;
    if (interval > 300 && interval < 2000) rrs.push(interval);
  }
  const mean_rr = rrs.length === 0 ? 0 : rrs.reduce((a, b) => a + b, 0) / rrs.length;
  const bpm = mean_rr === 0 ? 0 : Math.round(60000 / mean_rr);
  let sq_diff = 0;
  for (let i = 1; i < rrs.length; i++) {
    const a = rrs[i];
    const b = rrs[i - 1];
    if (a === undefined || b === undefined) continue;
    sq_diff += (a - b) ** 2;
  }
  const rmssd = rrs.length < 2 ? 0 : Math.sqrt(sq_diff / (rrs.length - 1));
  const last = samples[samples.length - 1]?.t ?? 0;
  const duration_s = last / 1000;
  let signal_quality: HrvResult['signal_quality'] = 'good';
  if (peaks.length < 8) signal_quality = 'poor';
  else if (peaks.length < 20 || duration_s < 20) signal_quality = 'fair';
  return {
    bpm,
    rmssd_ms: Math.round(rmssd),
    rr_intervals_ms: rrs.map((x) => Math.round(x)),
    sample_count: samples.length,
    duration_s: Math.round(duration_s * 10) / 10,
    signal_quality,
  };
}

function detrend(series: number[]): number[] {
  if (series.length === 0) return [];
  const n = series.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  for (let i = 0; i < n; i++) {
    const y = series[i] ?? 0;
    sumX += i;
    sumY += y;
    sumXY += i * y;
    sumX2 += i * i;
  }
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX || 1);
  const intercept = (sumY - slope * sumX) / n;
  return series.map((y, i) => y - (slope * i + intercept));
}

function movingAverage(series: number[], window: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < series.length; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - window); j <= Math.min(series.length - 1, i + window); j++) {
      sum += series[j] ?? 0;
      count++;
    }
    out.push(count === 0 ? 0 : sum / count);
  }
  return out;
}

function findPeaks(signal: number[], samples: readonly PpgSample[]): number[] {
  if (signal.length < 3) return [];
  const peaks: number[] = [];
  const mean = signal.reduce((a, b) => a + b, 0) / signal.length;
  const std = Math.sqrt(signal.reduce((a, b) => a + (b - mean) ** 2, 0) / signal.length);
  const threshold = mean + 0.25 * std;
  let lastPeakT = -Infinity;
  for (let i = 1; i < signal.length - 1; i++) {
    const s = signal[i];
    const p = signal[i - 1];
    const n = signal[i + 1];
    if (s === undefined || p === undefined || n === undefined) continue;
    if (s > p && s > n && s > threshold) {
      const t = samples[i]?.t ?? 0;
      if (t - lastPeakT > 300) {
        peaks.push(t);
        lastPeakT = t;
      }
    }
  }
  return peaks;
}

export function interpretHrv(r: HrvResult): { bucket: 'rested' | 'okay' | 'strained' | 'unknown'; line_hi: string; line_en: string } {
  if (r.signal_quality === 'poor' || r.rmssd_ms === 0) {
    return { bucket: 'unknown', line_hi: 'signal saaf nahi aaya', line_en: 'reading uncertain, retry' };
  }
  if (r.rmssd_ms >= 50) return { bucket: 'rested', line_hi: 'tanaav kam · dimaag taza', line_en: 'parasympathetic strong, well-rested' };
  if (r.rmssd_ms >= 30) return { bucket: 'okay', line_hi: 'normal range', line_en: 'within normal vagal tone' };
  return { bucket: 'strained', line_hi: 'tanaav zyada · neend kacchi', line_en: 'low HRV, recovery deficit' };
}
